// Planning calculators: CPP/OAS estimator, RRIF withdrawal, RESP withdrawal, FIRE number, MER impact.
import { round2, futureValue } from "./finance-math";
import { GOV_BENEFITS, rrifFactor } from "../financial-data/canada";

export function cppOasEstimate({ startAge = 65, cppPercentOfMax = 75, yearsInCanada = 40, oasStartAge = 65 }) {
  const cppBase = GOV_BENEFITS.cpp.maxMonthlyAt65 * (Math.max(0, Math.min(100, Number(cppPercentOfMax) || 0)) / 100);
  let cppAdj;
  const sa = Number(startAge) || 65;
  if (sa < 65) cppAdj = 1 - 0.006 * (65 - sa) * 12;
  else cppAdj = 1 + 0.007 * (sa - 65) * 12;
  const cppMonthly = Math.max(0, cppBase * cppAdj);

  const oasFraction = Math.max(0, Math.min(1, (Number(yearsInCanada) || 0) / 40));
  const oasBase = GOV_BENEFITS.oas.maxMonthly65to74 * oasFraction;
  const oa = Number(oasStartAge) || 65;
  const oasAdj = oa > 65 ? 1 + 0.006 * (oa - 65) * 12 : 1;
  const oasMonthly = oasBase * oasAdj;

  return {
    cppMonthly: round2(cppMonthly),
    oasMonthly: round2(oasMonthly),
    combinedMonthly: round2(cppMonthly + oasMonthly),
    combinedAnnual: round2((cppMonthly + oasMonthly) * 12),
    cppStartAge: sa, oasStartAge: oa,
  };
}

export function rrifWithdrawal({ balance, currentAge, annualReturn = 4, endAge = 95 }) {
  let bal = Math.max(0, Number(balance) || 0);
  const rate = Math.max(0, Number(annualReturn) || 0) / 100;
  const start = Number(currentAge) || 71;
  const end = Math.max(start + 1, Number(endAge) || 95);
  const series = [];
  let totalWithdrawn = 0, firstYear = 0;
  for (let age = start; age <= end && bal > 0.01; age++) {
    const factor = rrifFactor(age);
    const minWithdrawal = bal * factor;
    series.push({ age, factor: round2(factor * 100), minWithdrawal: round2(minWithdrawal), balance: round2(bal) });
    if (age === start) firstYear = minWithdrawal;
    bal = (bal - minWithdrawal) * (1 + rate);
    totalWithdrawn += minWithdrawal;
  }
  return {
    firstYearMin: round2(firstYear),
    firstYearFactor: round2(rrifFactor(start) * 100),
    totalWithdrawn: round2(totalWithdrawn),
    yearsOfIncome: series.length,
    series,
  };
}

export function respWithdrawal({ totalBalance, contributions, yearsOfStudy = 4, studentIncome = 15000 }) {
  const total = Math.max(0, Number(totalBalance) || 0);
  const contrib = Math.min(Math.max(0, Number(contributions) || 0), total);
  const eap = Math.max(0, total - contrib); // grants + growth = Educational Assistance Payments (taxable to student)
  const perYearEap = eap / Math.max(1, Number(yearsOfStudy) || 1);
  const perYearContrib = contrib / Math.max(1, Number(yearsOfStudy) || 1);
  // Students usually pay little/no tax thanks to basic + tuition credits.
  const likelyTaxFree = (Number(studentIncome) || 0) + perYearEap < 16000;
  return {
    totalBalance: round2(total),
    contributionsPortion: round2(contrib),
    eapPortion: round2(eap),
    perYearWithdrawal: round2((eap + contrib) / Math.max(1, Number(yearsOfStudy) || 1)),
    perYearEap: round2(perYearEap),
    perYearContrib: round2(perYearContrib),
    likelyTaxFree,
  };
}

export function fireNumber({ annualExpenses, withdrawalRate = 4, currentSavings = 0, annualContribution = 0, annualReturn = 6 }) {
  const exp = Math.max(0, Number(annualExpenses) || 0);
  const swr = Math.max(0.1, Number(withdrawalRate) || 4) / 100;
  const target = exp / swr;
  const rate = Math.max(0, Number(annualReturn) || 0) / 100;
  let bal = Math.max(0, Number(currentSavings) || 0);
  const contrib = Math.max(0, Number(annualContribution) || 0);
  let years = 0;
  const series = [{ year: 0, balance: round2(bal) }];
  while (bal < target && years < 100) {
    bal = bal * (1 + rate) + contrib;
    years += 1;
    series.push({ year: years, balance: round2(bal) });
  }
  const coastTarget = target / Math.pow(1 + rate, Math.max(1, years));
  return {
    fireNumber: round2(target),
    yearsToFire: bal >= target ? years : null,
    leanFire: round2((exp * 0.7) / swr),
    fatFire: round2((exp * 1.5) / swr),
    coastFireNow: round2(coastTarget),
    series,
  };
}

export function merImpact({ initial, annualContribution, annualReturn, years, highMER, lowMER }) {
  const grossRate = Math.max(0, Number(annualReturn) || 0) / 100;
  const run = (mer) => {
    const net = grossRate - Math.max(0, Number(mer) || 0) / 100;
    return futureValue({ rate: net, periods: Number(years) || 0, pmt: Number(annualContribution) || 0, pv: Number(initial) || 0 });
  };
  const high = run(highMER);
  const low = run(lowMER);
  const contributions = (Number(initial) || 0) + (Number(annualContribution) || 0) * (Number(years) || 0);
  const series = [];
  let bh = Number(initial) || 0, bl = Number(initial) || 0;
  const nh = grossRate - (Number(highMER) || 0) / 100, nl = grossRate - (Number(lowMER) || 0) / 100;
  for (let y = 0; y <= (Number(years) || 0); y++) {
    series.push({ x: `Yr ${y}`, "Higher MER": round2(bh), "Lower MER": round2(bl) });
    bh = bh * (1 + nh) + (Number(annualContribution) || 0);
    bl = bl * (1 + nl) + (Number(annualContribution) || 0);
  }
  return {
    valueHigh: round2(high), valueLow: round2(low),
    feeDrag: round2(low - high),
    feeDragPct: low ? round2(((low - high) / low) * 100) : 0,
    totalContributions: round2(contributions),
    series,
  };
}
